// frontend/src/components/reputation/FullAuditModal.jsx
// Version: 1.1
// Description: Модальное окно полного аудита репутации.
//
// 🔴 Просмотр отделён от исполнения: открытие окна не делает НИ ОДНОГО запроса,
//    живой платный прогон начинается только по нажатию кнопки запуска.
//
// 🔴 ПРАВИЛО НОЛЬ. Прежняя кнопка «Run Full Audit» крутила полосу
//    «Проверка 72 сигналов» от таймера, не сделав ни одного запроса. Здесь
//    полоса НЕ вычисляется на клиенте вообще: `progress` и состояния этапов
//    берутся из ответа сервера как есть. Единственный таймер в файле —
//    интервал опроса состояния; он спрашивает, а не рисует.
//
// 🔴 Ни одного адреса, телефона, номера Bar и ссылки на каталог: всё приходит
//    из ручки /api/v1/reputation/full-audit/*.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2, AlertTriangle, MinusCircle, ExternalLink } from 'lucide-react';

const OPROS_MS = 2000;

const ETAP_PODPIS = {
  nap: 'Согласованность NAP в каталогах',
  ahrefs: 'Метрики домена (Ahrefs)',
  geo: 'Опрос нейросетей',
  prescriptions: 'Сбор предписаний',
};

// Исход этапа. У «мы не спрашивали» свой вид: спутать его с «ответа нет»
// значит показать бездействие прибора как состояние дел.
const ISHOD = {
  PENDING: { text: 'ожидает', color: 'text-[#687077] bg-[#F8F7F2] border-[#E5E1D6]', Icon: MinusCircle },
  RUNNING: { text: 'идёт', color: 'text-sky-800 bg-sky-50 border-sky-200', Icon: Loader2 },
  OK: { text: 'готово', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 },
  NOT_CONFIGURED: { text: 'не спрашивали', color: 'text-amber-800 bg-amber-50 border-amber-200', Icon: AlertTriangle },
  FAILED: { text: 'отказ', color: 'text-rose-700 bg-rose-50 border-rose-200', Icon: AlertTriangle },
};

// В плоской сетке показываются ТОЛЬКО отдельные значения. Объект здесь либо
// раскрывается осознанно и отдельно (как `measurement`), либо не выводится
// вовсе. Правило по РОДУ значения, а не по списку имён: список устарел бы
// молча, и поле, добавленное завтра, снова стало бы `[object Object]`.
const skalyar = (v) => v === null || v === undefined
  || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

export const FullAuditModal = ({ open, onClose, tenant = 'fitenko', onCompleted }) => {
  const [sostoyanie, setSostoyanie] = useState(null);
  const [oshibka, setOshibka] = useState(null);
  const [zapushcheno, setZapushcheno] = useState(false);
  const taimerRef = useRef(null);

  const ostanovit = useCallback(() => {
    if (taimerRef.current) {
      clearInterval(taimerRef.current);
      taimerRef.current = null;
    }
  }, []);

  const sprosit = useCallback(async (taskId) => {
    try {
      const res = await axios.get(`/api/v1/reputation/full-audit/status/${taskId}`);
      setSostoyanie(res.data);
      if (res.data?.status === 'COMPLETED' || res.data?.status === 'FAILED') {
        ostanovit();
        if (res.data?.status === 'COMPLETED' && onCompleted) onCompleted(res.data);
      }
    } catch (err) {
      // 404 здесь означает «прогон не найден»: учёт живёт в памяти процесса и
      // не переживает перезапуск бэкенда. Это НЕ «аудит провалился».
      ostanovit();
      setOshibka(err?.response?.data?.detail || err?.message || 'Опрос состояния не удался');
    }
  }, [ostanovit, onCompleted]);

  // 🔴 Единственное место, откуда уходит живой платный прогон. Раньше это
  // тело висело в useEffect и тратило ресурсы самим фактом открытия окна.
  const zapustit = useCallback(async () => {
    if (taimerRef.current) return; // защита от второго запуска поверх идущего
    setOshibka(null);
    try {
      const res = await axios.post(`/api/v1/reputation/full-audit/run?tenant=${tenant}`);
      setSostoyanie(res.data);
      const taskId = res.data?.task_id;
      if (!taskId) {
        setOshibka('Сервер не вернул идентификатор прогона');
        return;
      }
      taimerRef.current = setInterval(() => sprosit(taskId), OPROS_MS);
      sprosit(taskId);
    } catch (err) {
      setOshibka(err?.response?.data?.detail || err?.message || 'Аудит не запустился');
    }
  }, [tenant, sprosit]);

  // Открытие окна только СБРАСЫВАЕТ показанное и убирает за собой при закрытии:
  // повторное открытие не наследует прошлый прогон и ничего не запускает.
  useEffect(() => {
    if (!open) return undefined;
    setOshibka(null);
    setSostoyanie(null);
    setZapushcheno(false);
    return () => { ostanovit(); };
  }, [open, ostanovit]);

  if (!open) return null;

  const etapy = sostoyanie?.stages || {};
  const otchet = sostoyanie?.report;
  // 🔴 Полоса берётся с сервера. Если её нет — прочерк, а не ноль и не своё число.
  const progress = typeof sostoyanie?.progress === 'number' ? sostoyanie.progress : null;

  // Разбор словаря метрик по РОДУ значения: скаляры — в сетку, `measurement` —
  // в отдельное осознанное раскрытие, остальные вложенные поля не выводятся.
  const ahrefsPary = otchet?.ahrefs ? Object.entries(otchet.ahrefs) : [];
  const ahrefsSkalyary = ahrefsPary.filter(([, v]) => skalyar(v));
  const zamer = otchet?.ahrefs?.measurement && !skalyar(otchet.ahrefs.measurement)
    ? otchet.ahrefs.measurement : null;
  // 🔴 Чтобы вложенное поле не исчезало БЕЗЗВУЧНО, назван их СЧЁТ. Счёт — не
  // значение и имён не называет: граница показанного объявлена, а сырой ответ
  // внешнего API на экран при этом не протекает.
  const nerazvernuto = ahrefsPary.filter(([k, v]) => !skalyar(v) && k !== 'measurement').length;
  const itogoGeo = otchet?.geo?._itogo;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto shadow-xl border border-[#E5E1D6]">
        <div className="flex items-start justify-between p-5 border-b border-[#F1EEE4] sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-serif text-[#20231F]">Полный аудит репутации</h2>
            <p className="text-xs text-[#687077] mt-0.5">
              Ход показывается по событиям сервера: полоса двигается, когда этап завершён, и никак иначе.
            </p>
          </div>
          <button onClick={() => { ostanovit(); onClose(); }}
            className="p-1.5 rounded-lg hover:bg-[#F8F7F2] text-[#687077] cursor-pointer" aria-label="Закрыть">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {oshibka && (
            <div className="border border-rose-200 bg-rose-50 rounded-xl p-4 text-xs text-rose-800">{oshibka}</div>
          )}

          <div className="border border-[#E5E1D6] bg-[#FCFBF8] rounded-xl p-4">
            <button
              type="button"
              onClick={() => { setZapushcheno(true); zapustit(); }}
              disabled={zapushcheno}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#20231F] text-white disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {zapushcheno ? 'Прогон идёт' : 'Запустить аудит'}
            </button>
            {/* 🔴 Состав прогона назван СЛОВАМИ. Ни числа вызовов, ни units:
                константы «тридцать вызовов» в коде нет, число подсказок и
                повторов берётся из конфига арендатора, а расход units мы не
                измеряли. Пустота видна, выдумка — нет. */}
            <p className="text-[11px] text-[#687077] mt-2 leading-snug">
              Прогон тратит платные ресурсы: обращение к платному Ahrefs и опрос
              нейросетей — по одному провайдеру на каждый заданный ключ. Сколько
              именно вызовов и units уйдёт — здесь не известно: число подсказок и
              повторов задаётся на сервере, а расход units мы не измеряли.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs text-[#687077] mb-1.5">
              <span>Ход</span>
              <span className="font-mono">{progress === null ? '—' : `${progress}%`}</span>
            </div>
            <div className="h-2 bg-[#F1EEE4] rounded-full overflow-hidden">
              <div className="h-full bg-[#20231F] transition-all duration-300"
                   style={{ width: `${progress === null ? 0 : progress}%` }} />
            </div>
          </div>

          <ul className="space-y-2">
            {Object.keys(ETAP_PODPIS).map((kluch) => {
              const st = etapy[kluch]?.status || 'PENDING';
              const meta = ISHOD[st] || ISHOD.PENDING;
              const Icon = meta.Icon;
              return (
                <li key={kluch} className="flex items-start gap-3">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border shrink-0 ${meta.color}`}>
                    <Icon className={`w-3 h-3 ${st === 'RUNNING' ? 'animate-spin' : ''}`} />
                    {meta.text}
                  </span>
                  <div className="text-xs text-[#20231F] leading-tight">
                    {ETAP_PODPIS[kluch]}
                    {etapy[kluch]?.detail && (
                      <div className="text-[10px] text-[#687077] mt-0.5">{etapy[kluch].detail}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {otchet && (
            <div className="space-y-4 border-t border-[#F1EEE4] pt-4">
              {otchet.nap && (
                <div className="bg-[#FCFBF8] border border-[#E5E1D6] rounded-xl p-4">
                  <div className="text-xs text-[#687077] uppercase tracking-wider">Каталоги</div>
                  <div className="text-sm text-[#20231F] mt-1">
                    Измерено {otchet.nap.measured_count} из {otchet.nap.total_count}
                    {' · '}индекс {otchet.nap.overall_score === null || otchet.nap.overall_score === undefined
                      ? '—' : `${otchet.nap.overall_score}%`}
                  </div>
                  {otchet.nap.measured_method === 'human_screenshot' && (
                    <div className="text-[10px] text-[#687077] mt-1">
                      метод замера: человек, снимки экрана · {otchet.nap.measured_at}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-[#FCFBF8] border border-[#E5E1D6] rounded-xl p-4">
                <div className="text-xs text-[#687077] uppercase tracking-wider">Метрики домена</div>
                {etapy.ahrefs?.status === 'NOT_CONFIGURED' ? (
                  // 🔴 Здесь запрещены нули и пустые графики: «ключа нет» —
                  // это утверждение о НАС, а ноль был бы утверждением о домене.
                  <div className="text-xs text-amber-800 mt-1">
                    API Key missing — {etapy.ahrefs?.detail || 'источник не настроен'}.
                    Ни одного значения не показано: не измеряли.
                  </div>
                ) : otchet.ahrefs ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                      {ahrefsSkalyary.map(([k, v]) => (
                        <div key={k}>
                          <div className="text-[10px] text-[#687077] uppercase">{k}</div>
                          <div className="text-sm font-mono text-[#20231F]">
                            {v === null || v === undefined ? '—' : String(v)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {nerazvernuto > 0 && (
                      <div className="text-[10px] text-[#687077] mt-2">
                        вложенных полей не показано: {nerazvernuto}
                      </div>
                    )}

                    {/* 🔴 Метаданные замера заведены ради того, чтобы числа не
                        сравнивали вслепую. Значения показываются так, КАК
                        ПРИШЛИ: второй способ отображения — второй держатель, он
                        разойдётся молча. Отсутствующее поле — не строка с
                        прочерком-заглушкой, а отсутствие строки. */}
                    {zamer && (
                      <div data-blok="zamer"
                           className="mt-3 pt-2 border-t border-[#E5E1D6] text-[11px] text-[#687077] space-y-0.5">
                        {(zamer.source || zamer.target) && (
                          <div>
                            {zamer.source && <span>чем измерено: {zamer.source}</span>}
                            {zamer.source && zamer.target && <span> · </span>}
                            {zamer.target && <span>по домену: {zamer.target}</span>}
                          </div>
                        )}
                        {zamer.measured_at && <div>момент замера: {zamer.measured_at}</div>}
                        {zamer.snapshot_date && (
                          <div>
                            снимок за {zamer.snapshot_date}
                            {zamer.snapshot_clock && <span> · часы: {zamer.snapshot_clock}</span>}
                            {zamer.snapshot_tz && <span> ({zamer.snapshot_tz})</span>}
                          </div>
                        )}
                        {zamer.country_filter_applied === false && (
                          <div>страновой фильтр не передавался: числа не по стране</div>
                        )}
                        {zamer.country_filter_applied === true && (
                          <div>страновой фильтр: {zamer.country ?? '—'}</div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-[#687077] mt-1">
                    {etapy.ahrefs?.detail || 'значений нет'}
                  </div>
                )}
              </div>

              {otchet.geo && (
                <div className="bg-[#FCFBF8] border border-[#E5E1D6] rounded-xl p-4">
                  <div className="text-xs text-[#687077] uppercase tracking-wider">Нейросети</div>
                  {otchet.geo_mode === 'express' && (
                    <div className="text-[10px] text-amber-800 mt-1">
                      экспресс-прогон: один повтор на подсказку вместо трёх — точность ниже,
                      с полным замером напрямую не сравнивать
                    </div>
                  )}
                  <div className="mt-2 space-y-1">
                    {Object.entries(otchet.geo)
                      .filter(([k]) => !k.startsWith('_'))
                      .map(([provider, v]) => (
                        <div key={provider} data-provider={provider}
                             className="flex items-start gap-2 text-xs">
                          <span className="font-medium text-[#20231F] w-24 shrink-0">{provider}</span>
                          {/* 🔴 `?? '—'` — именно `??`, не `||`: честный ноль
                              провайдера обязан печататься нулём. У веток «не
                              спрашивали» и «отказ» чисел нет ВОВСЕ: «нам не с
                              чем сравнивать» и «ноль» — разные утверждения. */}
                          <span className="text-[#687077]">
                            {v.status === 'NOT_CONFIGURED'
                              ? `не спрашивали — ${v.detail || 'не настроен'}`
                              : v.status === 'OK'
                                ? `подсказок опрошено: ${v.prompts_done ?? '—'}`
                                  + ` · наших ${v.our_citations ?? '—'} из ${v.total_citations ?? '—'} цитат`
                                  + ` · домен не определён: ${v.citations_domain_unknown ?? '—'}`
                                : `отказ: ${v.detail || ''}`}
                          </span>
                        </div>
                      ))}
                  </div>

                  {/* 🔴 Число опрошенных ответов здесь НЕ складывается: свой
                      счёт стал бы вторым держателем и разошёлся бы с бэкендом
                      молча. Показано ровно то, что положил бэкенд. */}
                  {itogoGeo ? (
                    <div data-blok="geo-itogo"
                         className="mt-2 pt-2 border-t border-[#E5E1D6] text-xs text-[#20231F]">
                      Итого: наших {itogoGeo.our_citations_total ?? '—'} из{' '}
                      {itogoGeo.citations_total ?? '—'} цитат (домен не определён:{' '}
                      {itogoGeo.citations_domain_unknown_total ?? '—'})
                      <div className="text-[10px] text-[#687077] mt-0.5">
                        ссылки на домен фирмы среди всех цитат, собранных у опрошенных провайдеров
                      </div>
                    </div>
                  ) : (
                    // Свода нет СОЗНАТЕЛЬНО, когда не ответил никто: ноль
                    // читался бы как «ссылок не нашлось».
                    <div className="mt-2 pt-2 border-t border-[#E5E1D6] text-[11px] text-[#687077]">
                      нейросети не опрашивались — свода нет
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(otchet.prescriptions) && otchet.prescriptions.length > 0 && (
                <div className="bg-white border border-[#E5E1D6] rounded-xl p-4">
                  <div className="text-xs text-[#687077] uppercase tracking-wider">
                    Что чинить ({otchet.prescriptions.length})
                  </div>
                  <ul className="mt-2 space-y-2">
                    {otchet.prescriptions.map((p, i) => (
                      <li key={`${p.directory_key}-${i}`} className="flex items-start justify-between gap-3">
                        <div className="text-xs">
                          <span className={p.severity === 'critical' ? 'text-rose-800 font-medium' : 'text-[#20231F]'}>
                            {p.directory_name}
                          </span>
                          <span className="text-[#687077]"> — {p.issue}</span>
                        </div>
                        {p.profile_url && (
                          <a href={p.profile_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#20231F] hover:underline shrink-0">
                            Открыть <ExternalLink className="w-3 h-3 text-[#C8A653]" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {sostoyanie?.storage === 'in_process_only' && (
                <div className="text-[10px] text-[#687077]">
                  Отчёт хранится в памяти процесса и не переживёт перезапуск бэкенда.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullAuditModal;
